import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { motion } from "framer-motion";
import { z } from "zod";

const emailSchema = z.string().trim().email("Informe um email válido").max(255);

interface Props {
  onNext: (email: string, leadId: string) => void;
  onBack?: () => void;
}

export function StepPreCadastro({ onNext, onBack }: Props) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError("");
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }

    setLoading(true);
    try {
      const leadId = crypto.randomUUID();
      const { error: dbError } = await supabase
        .from("lead_simulador" as any)
        .insert({ id: leadId, email: result.data } as any);

      if (dbError) throw dbError;
      onNext(result.data, leadId);
    } catch {
      setError("Erro ao salvar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const BlurReportTeaser = () => (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-40 select-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] blur-xl scale-110">
        <div className="max-w-md mx-auto p-8 border-4 border-foreground/20 space-y-8 bg-background">
          <div className="flex justify-between items-start">
            <div className="w-24 h-24 rounded-full border-8 border-primary/30" />
            <div className="space-y-2">
              <div className="w-32 h-6 bg-foreground/20" />
              <div className="w-24 h-4 bg-foreground/10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-foreground/5 border-2 border-foreground/10" />
            <div className="h-20 bg-foreground/5 border-2 border-foreground/10" />
          </div>
          <div className="space-y-3">
            <div className="w-full h-4 bg-foreground/20" />
            <div className="w-[80%] h-4 bg-foreground/10" />
            <div className="w-[90%] h-4 bg-foreground/10" />
          </div>
          <div className="h-40 bg-muted/50 rounded-lg flex items-center justify-center">
            <div className="w-[60%] h-[60%] bg-primary/20 rounded-full" />
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/60 to-background z-10" />
    </div>
  );

  return (
    <div className="relative min-h-[500px] flex items-center justify-center">
      <BlurReportTeaser />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-20 space-y-8 max-w-lg w-full bg-background/80 backdrop-blur-md p-8 border-2 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.1)]"
      >
        <div className="space-y-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary text-primary-foreground text-[10px] font-black uppercase tracking-widest mb-2">
            <ShieldCheck className="w-3 h-3" />
            Diagnóstico Processado
          </div>
          <h2 className="text-4xl font-heading font-black uppercase tracking-tighter leading-none">
            Análise <span className="text-primary italic">Finalizada.</span>
          </h2>
          <p className="text-muted-foreground text-sm font-bold uppercase tracking-tight">
            Cruzamento FIPE + Histórico de Leilões + Perícia Digital concluído com sucesso.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest opacity-60">Para onde enviamos o Laudo?</Label>
            <div className="relative group">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input
                id="email"
                type="email"
                placeholder="SEU_EMAIL@EXEMPLO.COM"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                className="h-14 pl-12 text-lg border-2 border-foreground group-hover:border-primary transition-colors bg-background font-black placeholder:opacity-30"
              />
            </div>
            {error && <p className="text-[10px] text-destructive font-black uppercase tracking-widest">{error}</p>}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-16 group font-black uppercase tracking-widest text-lg shadow-[4px_4px_0px_0px_rgba(var(--primary),0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            size="xl"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                Desbloquear Relatório
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </div>

        {onBack && (
          <Button variant="ghost" onClick={onBack} className="w-full text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground">
            ← Revisar dados da perícia
          </Button>
        )}

        <div className="flex items-center justify-center gap-2 pt-4 border-t border-border/50">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            Servidor Seguro • Criptografia de Ponta-a-Ponta
          </p>
        </div>
      </motion.div>
    </div>
  );
}
